export const PIXEL_WIPE_DURATION_MS = 800;

// Ported from Nominal's marketing-site "pixel" page transition (anime.js v4) to vanilla rAF.
// size => cell is ~5% of width (coarse grid); fuzziness => per-cell random offset range in rows.
const SIZE = 0.05;
const FUZZINESS = 0.5;

const easeOutQuad = (t: number) => t * (2 - t);

export type PixelWipeType = 'hide' | 'reveal';

type RunPixelWipeOptions = {
  /** `hide` erases a visible element away; `reveal` paints a hidden element in. */
  type?: PixelWipeType;
  duration?: number;
  /** Cell coarseness as a fraction of width (bigger = chunkier pixels). Defaults to SIZE. */
  size?: number;
  onComplete?: () => void;
};

/**
 * Animates a pixelated `clip-path: path(...)` made of jittered square cells over `el`.
 * The path holds the cells that should be PAINTED: for `hide` those are the still-covered cells
 * (so it dissolves away); for `reveal` those are the already-shown cells (so it paints in).
 * Returns a `cancel()` that stops the rAF loop — call it on unmount.
 */
export function runPixelWipe(
  el: HTMLElement,
  {
    type = 'hide',
    duration = PIXEL_WIPE_DURATION_MS,
    size = SIZE,
    onComplete,
  }: RunPixelWipeOptions = {},
): () => void {
  const { width: rawWidth, height: rawHeight } = el.getBoundingClientRect();
  const width = Math.max(1, Math.round(rawWidth));
  const height = Math.max(1, Math.round(rawHeight));

  const u = Math.max(5, Math.floor(width * size)); // cell size in px
  const cols = Math.ceil(width / u);
  const rows = Math.ceil(height / u);
  const fuzz = rows * FUZZINESS; // jitter range, in rows

  // Snapshot a random offset per cell once for this run.
  const jitter = new Float32Array(rows * cols);
  for (let i = 0; i < jitter.length; i += 1) jitter[i] = Math.random() * fuzz;

  const end = rows + fuzz;
  const travel = end - -fuzz; // frontier sweeps from `end` down to `-fuzz`

  const isHide = type === 'hide';

  // hide: starts fully painted (visible); reveal: starts fully clipped (nothing painted)
  el.style.clipPath = isHide ? 'inset(0%)' : 'inset(100%)';
  el.style.visibility = 'visible';

  let rafId = 0;
  let start = 0;
  let cancelled = false;

  const tick = (now: number) => {
    if (cancelled) return;
    if (!start) start = now;

    const linear = Math.min(1, (now - start) / duration);
    const frontier = end - travel * easeOutQuad(linear);

    let path = '';
    for (let row = 0; row < rows; row += 1) {
      // skip rows that contribute nothing on each side of the jitter band
      if (isHide && row > frontier + fuzz + 1) break;
      if (!isHide && row < frontier - fuzz - 1) continue;
      for (let col = 0; col < cols; col += 1) {
        const threshold = frontier + jitter[row * cols + col];
        const painted = isHide ? row < threshold : row > threshold;
        if (painted) {
          // full square; +1 overlap so adjacent cells abut with no hairline seams
          const x = col * u;
          const y = row * u;
          path += `M ${x} ${y} L ${x + u + 1} ${y} L ${x + u + 1} ${y + u + 1} L ${x} ${y + u + 1} Z `;
        }
      }
    }

    el.style.clipPath = path ? `path('${path.trim()}')` : 'inset(100%)';

    if (linear < 1) {
      rafId = requestAnimationFrame(tick);
    } else if (isHide) {
      el.style.clipPath = 'inset(100%)';
      el.style.visibility = 'hidden';
      onComplete?.();
    } else {
      // fully painted — show everything (explicit inset(0%) beats the consumer's initial-hidden class)
      el.style.clipPath = 'inset(0%)';
      onComplete?.();
    }
  };

  rafId = requestAnimationFrame(tick);

  return () => {
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
  };
}
